import { DossierDetails, useGetNazorg } from "@/entities/dossier-details";
import { RoleGate, useAuth, UserRole } from "@/features/auth";
import { useFeatureFlagValue } from "@/features/config";
import {
    ActionsDropdown,
    Badge,
    Button,
    DropdownMenuItem,
    DtLabel,
    Icon,
    Skeleton,
    toast,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/shared/components";
import { copyToClipboard, formatDateTimeRelative } from "@/shared/utils";
import { isDate } from "date-fns";
import { useState } from "react";
import { DossierSummaryCard, DossierSummaryCardSkeleton } from "./dossier-summary-card";
import { NazorgDialog } from "./nazorg-dialog";
import { NewEditPrognoseDialog } from "./new-edit-prognose-dialog";

type DossierMainProps = {
    dossierId: string;
    dossierDetails: DossierDetails;
};

export function DossierMain({ dossierId, dossierDetails }: Readonly<DossierMainProps>) {
    const [prognoseDialogOpen, setPrognoseDialogOpen] = useState(false);
    const { startDatumTijd: startDatumTijdSein, treinIncidentScenario, prognose, spoorweb } = dossierDetails;
    const fallback = spoorweb ? "Niet bekend" : null;
    const startDatumTijdSpoorweb = optionalSpoorwebPropertyToString(spoorweb?.startDatumTijd, fallback);
    const spoorwebNummer = optionalSpoorwebPropertyToString(spoorweb?.nummer, fallback);
    const planaanpassing = optionalSpoorwebPropertyToString(spoorweb?.planaanpassing, fallback);
    const treinIncidentScenarioCode = optionalSpoorwebPropertyToString(treinIncidentScenario?.code, fallback);
    const treindienstLeiderGebied = optionalSpoorwebPropertyToString(dossierDetails.treindienstLeiderGebied, fallback);

    const handleCopySpoorweb = () => {
        void copyToClipboard(spoorwebNummer!);
        toast("Spoorwebnummer gekopieerd");
    };

    const handleCopySein = () => {
        void copyToClipboard(dossierDetails.id.toString());
        toast("SEIN nummer gekopieerd");
    };

    return (
        <DossierSummaryCard section="DossierDetails" headerSlot={<NazorgHeaderSlot dossierId={dossierId} />}>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 pr-3 pl-4 md:grid-cols-3">
                <div className="order-1">
                    <DtLabel className="mb-1">Start SEIN dossier</DtLabel>
                    <dd className="flex gap-1.75">
                        <span>{formatDateTimeRelative(startDatumTijdSein)}</span>
                    </dd>
                </div>
                {startDatumTijdSpoorweb && (
                    <div className="order-2">
                        <DtLabel className="mb-1">Start Spoorweb dossier</DtLabel>
                        <dd className="flex gap-1.75">
                            <span>
                                {typeof startDatumTijdSpoorweb === "object"
                                    ? formatDateTimeRelative(startDatumTijdSpoorweb)
                                    : startDatumTijdSpoorweb}
                            </span>
                        </dd>
                    </div>
                )}
                <div className="order-3">
                    <DtLabel className="mb-1 flex items-center">
                        Prognose verstoring
                        <RoleGate roles={[UserRole.REGIEVOERDER]}>
                            <Button
                                variant="tertiary"
                                iconOnly
                                icon={!prognose ? "clock-plus" : "edit"}
                                data-testid={!prognose ? "add-prognose-button" : "update-prognose-button"}
                                size="s"
                                iconSize="xs"
                                onClick={() => setPrognoseDialogOpen(true)}
                            />
                        </RoleGate>
                    </DtLabel>
                    <dd>
                        <span className="flex items-center gap-2">
                            {prognose?.datumTijd ? formatDateTimeRelative(prognose?.datumTijd) : "Niet bekend"}
                            {prognose?.status && (
                                <Badge variant="gray-subtle" size="m">
                                    {prognose.status}
                                </Badge>
                            )}
                            {prognose?.toelichting && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Icon name="comment" size="xs" />
                                    </TooltipTrigger>
                                    <TooltipContent>{prognose?.toelichting}</TooltipContent>
                                </Tooltip>
                            )}
                        </span>
                    </dd>
                </div>
                <div className={spoorweb ? "order-3" : "order-2"}>
                    <DtLabel className="mb-1">SEIN nr</DtLabel>
                    <dd className="flex items-center gap-2.5">
                        <span>{dossierDetails.id}</span>
                        <Button
                            iconOnly={true}
                            variant="tertiary"
                            size="s"
                            iconSize="xs"
                            onClick={handleCopySein}
                            className="border-0 p-0"
                            title="Kopieer"
                        >
                            <Icon name="copy" className="text-content-interactive-default" />
                        </Button>
                    </dd>
                </div>
                {spoorwebNummer && (
                    <div className="order-5">
                        <DtLabel className="mb-1">Spoorweb nr</DtLabel>
                        <dd className="flex items-center gap-2.5">
                            <span>{spoorwebNummer}</span>
                            <Button
                                iconOnly={true}
                                variant="tertiary"
                                size="s"
                                iconSize="xs"
                                onClick={handleCopySpoorweb}
                                className="border-0 p-0"
                                title="Kopieer"
                            >
                                <Icon name="copy" className="text-content-interactive-default" />
                            </Button>
                        </dd>
                    </div>
                )}
                {treinIncidentScenarioCode && (
                    <div className="order-6">
                        <DtLabel className="mb-1">TIS</DtLabel>
                        <dd>{treinIncidentScenarioCode}</dd>
                    </div>
                )}
                {treindienstLeiderGebied && (
                    <div className="order-7">
                        <DtLabel className="mb-1">Treindienstleidergebied</DtLabel>
                        <dd>{treindienstLeiderGebied}</dd>
                    </div>
                )}
                {planaanpassing && (
                    <div className="order-8 col-span-2">
                        <DtLabel className="mb-1">Planaanpassing</DtLabel>
                        <dd>{planaanpassing}</dd>
                    </div>
                )}
            </dl>
            {prognoseDialogOpen && (
                <NewEditPrognoseDialog
                    dossierId={dossierId}
                    open={prognoseDialogOpen}
                    setOpen={setPrognoseDialogOpen}
                    defaultValues={{
                        datumTijd: prognose?.datumTijd,
                        status: prognose?.status,
                        toelichting: prognose?.toelichting,
                    }}
                />
            )}
        </DossierSummaryCard>
    );
}

export function DossierMainSkeleton() {
    return (
        <DossierSummaryCardSkeleton section="DossierDetails" data-testid="dossier-details-skeleton">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 xl:grid-cols-3">
                <div>
                    <DtLabel className="mb-1">Spoorweb nr</DtLabel>
                    <dd className="flex items-center gap-2.5">
                        <Skeleton className="text-content-emphasis h-4 w-20" />
                    </dd>
                </div>
                <div>
                    <DtLabel className="mb-1">Start Spoorweb dossier</DtLabel>
                    <dd className="flex gap-1.75">
                        <Icon name="calendar" className="text-content-emphasis" />
                        <Skeleton className="h-4 w-20" />
                    </dd>
                </div>
                <div>
                    <DtLabel className="mb-1">Start Sein dossier</DtLabel>
                    <dd className="flex gap-1.75">
                        <Icon name="calendar" className="text-content-emphasis" />
                        <Skeleton className="h-4 w-20" />
                    </dd>
                </div>
                <div>
                    <DtLabel className="mb-1">Treindienstleidergebied</DtLabel>
                    <dd>
                        <Skeleton className="h-4 w-20" />
                    </dd>
                </div>
                <div>
                    <DtLabel className="mb-1">TIS</DtLabel>
                    <dd>
                        <Skeleton className="h-4 w-20" />
                    </dd>
                </div>
                <div>
                    <DtLabel className="mb-1">ProRail prognose</DtLabel>
                    <dd className="flex gap-1.75">
                        <Icon name="clock" className="text-content-emphasis" />
                        <span>
                            <Skeleton className="h-4 w-20" />
                        </span>
                    </dd>
                </div>
                <div className="col-span-full">
                    <DtLabel className="mb-1">Planaanpassing</DtLabel>
                    <dd>
                        <Skeleton className="h-4 w-20" />
                    </dd>
                </div>
            </dl>
        </DossierSummaryCardSkeleton>
    );
}

type HeaderSlotProps = {
    dossierId: string;
};

function NazorgHeaderSlot({ dossierId }: HeaderSlotProps) {
    const nazorgEnabled = useFeatureFlagValue("NAZORG_ENABLED", false);
    const { hasRole } = useAuth();
    const [nazorgDialogOpen, setNazorgDialogOpen] = useState(false);
    const { data: nazorg, isPending, error } = useGetNazorg(dossierId, nazorgEnabled);

    if (isPending || error || !nazorgEnabled) return;

    const isRegievoerder = hasRole(UserRole.REGIEVOERDER);
    const hasBadgeRole = [UserRole.REGIEVOERDER, UserRole.SUPPORTCONSULTANT, UserRole.KLANTENSERVICE, UserRole.ALLEENLEZEN].some((role) =>
        hasRole(role)
    );

    if (isRegievoerder && !nazorg) {
        return (
            <>
                <ActionsDropdown data-testid="dossier-summary-actions">
                    <DropdownMenuItem onSelect={() => setNazorgDialogOpen(true)}>Nazorg bij incidenten activeren</DropdownMenuItem>
                </ActionsDropdown>
                {nazorgDialogOpen && <NazorgDialog dossierId={dossierId} open={nazorgDialogOpen} setOpen={setNazorgDialogOpen} />}
            </>
        );
    }

    if (hasBadgeRole && nazorg) {
        return (
            <Badge variant="gray" data-testid="nazorg-geactiveerd" title={nazorg.betrokkenTreinen.join(", ")}>
                Nazorg geactiveerd
            </Badge>
        );
    }

    return null;
}

/**
 * @param property to be converted to string. Dates automatically converted with `formatDateRelative`
 * @param fallback When you don't want any fallback and hide fields when they're undefined, use `null`
 */
function optionalSpoorwebPropertyToString<T extends string | Date | string[]>(
    property: T | undefined,
    fallback: string | null
): string | null {
    if (!property) return fallback;

    if (isDate(property)) return formatDateTimeRelative(property);
    if (Array.isArray(property)) return property.length > 0 ? property.join(", ") : fallback;

    return property;
}
